import { useState, type ReactNode } from 'react';
import { UMCheckbox, UMIcon, UMR } from '../_components';
import { findNode, leafIds } from './exportTree';
import type { ExportTreeNode } from './types';

/**
 * Local variant of UMCheckboxTree (Export › Données à exporter): same measured structure
 * (parent indeterminate, children `pl-6`, `expand_less` toggle at -24px, 18px glyph / 28px line)
 * plus `defaultCollapsed` so the layer groups can open collapsed like in the Couches menu.
 */
export interface ExportCheckboxTreeProps {
  nodes: ExportTreeNode[];
  checked: string[];
  onChange: (checked: string[]) => void;
  /** Node ids collapsed on first render. */
  defaultCollapsed?: string[];
  /**
   * Mode "couche" : la case racine active/désactive toute la couche (indépendamment de ses sous-éléments).
   * Désactivée, les sous-éléments sont affichés décochés et grisés.
   */
  rootEnabled?: boolean;
  onRootEnabledChange?: (enabled: boolean) => void;
}

export function ExportCheckboxTree({ nodes, checked, onChange, defaultCollapsed = [], rootEnabled, onRootEnabledChange }: ExportCheckboxTreeProps) {
  const [collapsed, setCollapsed] = useState<string[]>(defaultCollapsed);
  const set = new Set(checked);
  const layerMode = rootEnabled !== undefined;

  const toggle = (node: ExportTreeNode, on: boolean) => {
    const ids = leafIds(node).filter((id) => !findNode(nodes, id)?.locked);
    const next = new Set(set);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    onChange([...next]);
  };

  const render = (node: ExportTreeNode, depth: number): ReactNode => {
    const leaves = leafIds(node);
    const isLayerRoot = layerMode && depth === 0;
    const layerOff = layerMode && depth > 0 && !rootEnabled;
    const n = layerOff ? 0 : leaves.filter((id) => set.has(id)).length;
    const all = isLayerRoot ? Boolean(rootEnabled) : n === leaves.length;
    const isParent = Boolean(node.children?.length);
    const open = !collapsed.includes(node.id);
    return (
      <div key={node.id}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {isParent && depth > 0 && (
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? `Réduire ${node.label}` : `Développer ${node.label}`}
              title={open ? 'Réduire la catégorie' : 'Développer la catégorie'}
              onClick={() => setCollapsed((c) => (open ? [...c, node.id] : c.filter((id) => id !== node.id)))}
              style={{ position: 'absolute', left: -24, height: 24, width: 16, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <UMIcon name={open ? 'expand_less' : 'expand_more'} family="icons" size={18} color={UMR.black} style={{ height: 28 }} />
            </button>
          )}
          <span style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', opacity: node.locked || layerOff ? 0.5 : 1 }}>
            <UMCheckbox
              checked={all}
              indeterminate={!isLayerRoot && isParent && n > 0 && !all}
              disabled={node.locked || layerOff}
              onChange={(on) => (isLayerRoot ? onRootEnabledChange?.(on) : toggle(node, on))}
              label={<span title={node.label} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.label}</span>}
            />
          </span>
        </div>
        {isParent && open && <div style={{ paddingLeft: 24 }}>{node.children!.map((c) => render(c, depth + 1))}</div>}
      </div>
    );
  };

  return <div>{nodes.map((n) => render(n, 0))}</div>;
}
